import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Plus, Pencil, Trash2, BookOpen, X } from "lucide-react";
import type { RawMaterial, BaseRecipeWithItems } from "@shared/schema";

const recipeFormSchema = z.object({
  name: z.string().min(1, "Namn krävs"),
  description: z.string().optional(),
});

type RecipeFormData = z.infer<typeof recipeFormSchema>;

type RecipeItemInput = {
  rawMaterialId: string;
  weight: number;
};

export default function AdminBaseRecipes() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<BaseRecipeWithItems | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [recipeItems, setRecipeItems] = useState<RecipeItemInput[]>([]);
  const [newItemMaterialId, setNewItemMaterialId] = useState("");
  const [newItemWeight, setNewItemWeight] = useState("");

  const { data: recipes, isLoading } = useQuery<BaseRecipeWithItems[]>({
    queryKey: ["/api/admin/base-recipes"],
  });

  const { data: rawMaterials } = useQuery<RawMaterial[]>({
    queryKey: ["/api/admin/raw-materials"],
  });

  const form = useForm<RecipeFormData>({
    resolver: zodResolver(recipeFormSchema),
    defaultValues: { name: "", description: "" },
  });

  const createMutation = useMutation({
    mutationFn: (data: RecipeFormData & { items: RecipeItemInput[] }) =>
      apiRequest("POST", "/api/admin/base-recipes", data),
    onSuccess: () => {
      toast({ title: "Basrecept skapat!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/base-recipes"] });
      setDialogOpen(false);
      form.reset();
      setRecipeItems([]);
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte skapa basrecept.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: RecipeFormData & { id: string; items: RecipeItemInput[] }) =>
      apiRequest("PATCH", `/api/admin/base-recipes/${data.id}`, data),
    onSuccess: () => {
      toast({ title: "Basrecept uppdaterat!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/base-recipes"] });
      setDialogOpen(false);
      setEditingRecipe(null);
      form.reset();
      setRecipeItems([]);
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte uppdatera basrecept.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/base-recipes/${id}`),
    onSuccess: () => {
      toast({ title: "Basrecept raderat!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/base-recipes"] });
      setDeletingId(null);
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte radera basrecept.", variant: "destructive" });
    },
  });

  const openCreate = () => {
    setEditingRecipe(null);
    form.reset({ name: "", description: "" });
    setRecipeItems([]);
    setDialogOpen(true);
  };

  const openEdit = (recipe: BaseRecipeWithItems) => {
    setEditingRecipe(recipe);
    form.reset({ name: recipe.name, description: recipe.description || "" });
    setRecipeItems(
      recipe.items.map((item) => ({
        rawMaterialId: item.rawMaterialId,
        weight: parseFloat(item.weight),
      }))
    );
    setDialogOpen(true);
  };

  const addItem = () => {
    if (!newItemMaterialId || !newItemWeight) return;
    setRecipeItems((prev) => [
      ...prev,
      { rawMaterialId: newItemMaterialId, weight: parseFloat(newItemWeight) },
    ]);
    setNewItemMaterialId("");
    setNewItemWeight("");
  };

  const removeItem = (index: number) => {
    setRecipeItems((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = (data: RecipeFormData) => {
    if (editingRecipe) {
      updateMutation.mutate({ ...data, id: editingRecipe.id, items: recipeItems });
    } else {
      createMutation.mutate({ ...data, items: recipeItems });
    }
  };

  const getMaterialName = (id: string) => rawMaterials?.find((m) => m.id === id)?.name || "Okänd";
  const getMaterialPrice = (id: string) => parseFloat(rawMaterials?.find((m) => m.id === id)?.price || "0");

  const calculateItemCost = (item: RecipeItemInput) => {
    const pricePerKg = getMaterialPrice(item.rawMaterialId);
    return (item.weight * pricePerKg) / 1000;
  };

  const totalCost = recipeItems.reduce((sum, item) => sum + calculateItemCost(item), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Basrecept</h1>
        <p className="text-muted-foreground">Hantera basrecept med råvaror och priskalkyl</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <CardTitle>Receptlista</CardTitle>
            <CardDescription>Alla basrecept</CardDescription>
          </div>
          <Button onClick={openCreate} data-testid="button-add-base-recipe">
            <Plus className="h-4 w-4 mr-2" />
            Nytt basrecept
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : recipes && recipes.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Namn</TableHead>
                    <TableHead>Beskrivning</TableHead>
                    <TableHead>Ingredienser</TableHead>
                    <TableHead>Totalpris</TableHead>
                    <TableHead className="text-right">Åtgärd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recipes.map((recipe) => (
                    <TableRow key={recipe.id}>
                      <TableCell className="font-medium">{recipe.name}</TableCell>
                      <TableCell>{recipe.description || "-"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {recipe.items.map((item, i) => (
                            <Badge key={i} variant="secondary">
                              {item.rawMaterial?.name}: {item.weight}g
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>{recipe.totalPrice?.toFixed(2) || "0.00"} kr</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 flex-wrap">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(recipe)} data-testid={`button-edit-recipe-${recipe.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <AlertDialog open={deletingId === recipe.id} onOpenChange={(open) => !open && setDeletingId(null)}>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeletingId(recipe.id)} data-testid={`button-delete-recipe-${recipe.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Radera basrecept?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Detta kommer att permanent radera {recipe.name}.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteMutation.mutate(recipe.id)} className="bg-destructive text-destructive-foreground">
                                  Radera
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Inga basrecept registrerade</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRecipe ? "Redigera basrecept" : "Nytt basrecept"}</DialogTitle>
            <DialogDescription>
              {editingRecipe ? "Uppdatera receptets ingredienser" : "Skapa ett nytt basrecept med ingredienser"}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Namn</FormLabel>
                    <FormControl>
                      <Input {...field} data-testid="input-recipe-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beskrivning</FormLabel>
                    <FormControl>
                      <Textarea {...field} data-testid="input-recipe-description" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-3 rounded-lg border p-3">
                <p className="text-sm font-medium">Ingredienser</p>

                {recipeItems.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Råvara</TableHead>
                        <TableHead>Vikt (g)</TableHead>
                        <TableHead>Kostnad</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recipeItems.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{getMaterialName(item.rawMaterialId)}</TableCell>
                          <TableCell>{item.weight}g</TableCell>
                          <TableCell>{calculateItemCost(item).toFixed(2)} kr</TableCell>
                          <TableCell>
                            <Button size="icon" variant="ghost" type="button" onClick={() => removeItem(index)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={2} className="font-medium">Totalt</TableCell>
                        <TableCell className="font-medium">{totalCost.toFixed(2)} kr</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}

                <div className="flex items-end gap-2 flex-wrap">
                  <div className="flex-1">
                    <label className="text-xs text-muted-foreground">Råvara</label>
                    <Select value={newItemMaterialId} onValueChange={setNewItemMaterialId}>
                      <SelectTrigger data-testid="select-recipe-material">
                        <SelectValue placeholder="Välj råvara" />
                      </SelectTrigger>
                      <SelectContent>
                        {rawMaterials?.map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.name} ({m.price} kr/kg)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-24">
                    <label className="text-xs text-muted-foreground">Vikt (g)</label>
                    <Input
                      type="number"
                      value={newItemWeight}
                      onChange={(e) => setNewItemWeight(e.target.value)}
                      placeholder="g"
                      data-testid="input-recipe-item-weight"
                    />
                  </div>
                  <Button type="button" size="icon" variant="outline" onClick={addItem} data-testid="button-add-recipe-item">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Avbryt
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} data-testid="button-save-recipe">
                  {(createMutation.isPending || updateMutation.isPending) ? "Sparar..." : "Spara"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
